import React, { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import withRouter from "components/Common/withRouter";
import {
  Card,
  CardBody,
  Col,
  Container,
  Button,
  Input,
  Row,
} from "reactstrap";
import Select from "react-select";
import {
  AsyncClientSelect,
  AsyncSourceSelect,
  AsyncUserSelect,
} from "components/Common/AsyncSearchSelects";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import {
  DEFAULT_PAGE_SIZE,
  urlEndPoint,
  pascalToCamel,
  phoneNumberShowFormat,
  mapApiMetadataToTableColumnDefinition,
} from "helpers/GeneralHelper";
import { toast } from "react-toastify";

import AllCommonFilterModal from "pages/AllCommonFilter/all-common-filter";
import AddEditCandidateModal from "pages/AllCommonModal/AddEditCandidateModal/add-edit-candidate-modal";
import DriverDetailsModal from "pages/AllCommonModal/DriverDetailsModal/driver-details-modal";
import CandidateClosureModal from "pages/AllCommonModal/CandidateClosureModal/candidate-closure-modal";
import CandidateMarkAsHiredModal from "pages/AllCommonModal/CandidateMarkAsHiredModal/candidate-mark-as-hired-modal";
import CandidateMarkAsRejectedModal from "pages/AllCommonModal/CandidateMarkAsRejectedModal/candidate-mark-as-rejected-modal";
import CandidateOnboardingTableContainer from "components/Common/CandidateOnboardingTableContainer";
import MetadataFilterToolbar from "components/Common/MetadataFilterToolbar";
import CandidateListFilterPrompt from "components/Common/CandidateListFilterPrompt";
import {
  getCandidateMetadata,
  candidateTodayCall,
  candidateSureMailSend,
  resetCandidate,
  getCandidateDetailsForUsingCallDetails,
  fetchCommonOrderStatusOptions,
  submitCandidateFilterData,
} from "model/CandidateModel";
import Swal from "sweetalert2";
import Loaders from "components/Common/Loaders";
import { useCommonFilterOptions } from "libs/context/CommonFilterOptionsContext";
import { usePermissionContext } from "libs/context/PermissionContext";
import {
  getAsyncFilterLabelFieldKey,
  isAsyncFilterLabelFieldKey,
  isMetadataAsyncSelectFilterDefinition,
  countActiveMetadataFilters,
  mapFilterDefinitionsToModalData,
  mergeModalSubmitIntoListFilter,
  normalizeMetadataFilterDateForApi,
} from "helpers/metadataFilterHelpers";


const DEFAULT_CANDIDATE_LIST_FILTER = {
  search: "",
  status: "",
  statusId: "",
  orderStatus: "",
  profileStatus: "",
  reason: "",
  clientId: "",
  jobId: "",
  sourceId: "",
  userId: "",
  fromDate: null,
  toDate: null,
  fadvSend: "",
  isFadvSent: "",
};

const STATUS_OPTIONS = [
  { value: "active", label: "Active" },
  { value: "deactive", label: "Deactive" },
];

const PROFILE_STATUS_OPTIONS = [
  { value: "not started", label: "Not Started" },
  { value: "in progress", label: "In Progress" },
  { value: "completed", label: "Completed" },
  { value: "existing fedex driver", label: "Existing Fedex Driver" },
  { value: "offer sent", label: "Offer Sent" },
  { value: "hired", label: "Hired" },
  { value: "road test ready", label: "Road Test Ready" },
  { value: "not interested", label: "Not Interested" },
];

const REASON_OPTIONS = [
  { value: "distance", label: "Distance" },
  { value: "pay", label: "Pay" },
  { value: "schedule", label: "Schedule" },
  { value: "other", label: "Other" },
];

const FADV_SEND_OPTIONS = [
  { value: "", label: "All" },
  { value: "true", label: "Yes" },
  { value: "false", label: "No" },
];

/** Raw API keys for status columns shown on the candidates table (reset / row patch). */
const CANDIDATE_ROW_STATUS_SOURCE_KEYS = [
  "profileStatus",
  "orderStatus",
  "mecStatus",
  "drugStatus",
  "updateSend",
  "fadvSend",
  "isFadvSent",
  "funnelStatus",
  "funnlStatus",
  "candidateStatus",
  "status",
];

/** Empty / whitespace-only strings → null for API (no empty strings on filter fields). */
const normalizeOptionalStringFilterValueOrNull = (rawValue) => {
  if (rawValue === null || rawValue === undefined) return null;
  const trimmed = String(rawValue).trim();
  return trimmed === "" ? null : trimmed;
};

/** Fixed POST keys normalized on every candidates filter request before calling the backend. */
const CANDIDATE_FILTER_POST_BODY_STRING_KEYS_THAT_NORMALIZE_EMPTY_TO_NULL = [
  "nameContains",
  "emailContains",
  "status",
  "orderStatus",
  "profileStatus",
  "reason",
  "clientId",
  "jobId",
  "sourceId",
  "userId",
];

/**
 * Candidate/filter POST body: optional filters as null when not set.
 * Preserves pageNumber, pageSize, and boolean fadvSend.
 * `metadataFiltersFromApi` augments normalization so dynamically named filters (metadata `field`)
 * are also stored as JSON null rather than leftover empty strings when cleared.
 */
const normalizeCandidateFilterPayload = (rawCandidateFilterBody = {}, metadataFiltersFromApi = []) => {
  const outboundPayload = { ...rawCandidateFilterBody };

  /** Dynamic field keys from Candidate metadata `/filters`; `search` is handled via email/name contains. */
  const dynamicNormalizeKeysFromMetadata = (
    Array.isArray(metadataFiltersFromApi) ? metadataFiltersFromApi : []
  ).reduce((uniqueKeyAccumulator, filterDefinitionRow) => {
    const backendFieldIdentifier = filterDefinitionRow?.field;
    if (
      backendFieldIdentifier &&
      backendFieldIdentifier !== "search" &&
      !uniqueKeyAccumulator.includes(backendFieldIdentifier)
    ) {
      return [...uniqueKeyAccumulator, backendFieldIdentifier];
    }
    return uniqueKeyAccumulator;
  }, []);

  const normalizedStringKeywordKeysThatShouldBecomeNullWhenBlank = Array.from(
    new Set([
      ...CANDIDATE_FILTER_POST_BODY_STRING_KEYS_THAT_NORMALIZE_EMPTY_TO_NULL,
      ...dynamicNormalizeKeysFromMetadata,
    ])
  );

  normalizedStringKeywordKeysThatShouldBecomeNullWhenBlank.forEach((keywordKeyFromPayload) => {
    if (keywordKeyFromPayload in outboundPayload) {
      outboundPayload[keywordKeyFromPayload] = normalizeOptionalStringFilterValueOrNull(
        outboundPayload[keywordKeyFromPayload]
      );
    }
  });
  if (!outboundPayload.fromDate && outboundPayload.fromDate !== 0) outboundPayload.fromDate = null;
  else if (outboundPayload.fromDate === "") outboundPayload.fromDate = null;
  if (!outboundPayload.toDate && outboundPayload.toDate !== 0) outboundPayload.toDate = null;
  else if (outboundPayload.toDate === "") outboundPayload.toDate = null;

  const fadvSendFilterFromPayload = outboundPayload.fadvSend;
  if (fadvSendFilterFromPayload === true || fadvSendFilterFromPayload === false) {
    outboundPayload.fadvSend = fadvSendFilterFromPayload;
  } else if (fadvSendFilterFromPayload === "true") outboundPayload.fadvSend = true;
  else if (fadvSendFilterFromPayload === "false") outboundPayload.fadvSend = false;
  else outboundPayload.fadvSend = null;

  Object.keys(outboundPayload).forEach((payloadKey) => {
    if (isAsyncFilterLabelFieldKey(payloadKey)) {
      delete outboundPayload[payloadKey];
    }
  });

  outboundPayload.statusId = "deactive";

  return outboundPayload;
};

const DeactivateCandidate = () => {
  const navigate = useNavigate();
  const { permissionData } = usePermissionContext() || {};
  /** `getDropdownOptionsByApiKey`: loads metadata `dropdown`/`api` options for the toolbar (same endpoints as AllCommonFilterModal). */
  const { fetchMetadataSchema, getDropdownOptionsByApiKey } = useCommonFilterOptions();

  const [candidateListFilter, setCandidateListFilter] = useState({
    ...DEFAULT_CANDIDATE_LIST_FILTER,
  });
  const [candidateData, setCandidateData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [candidateFilterApplied, setCandidateFilterApplied] = useState(false);
  const [commonFilterModalOpen, setCommonFilterModalOpen] = useState(false);
  const [commonResetFilter, setCommonResetFilter] = useState(false);
  const [requestPayload, setRequestPayload] = useState({
    pageNumber: 1,
    pageSize: DEFAULT_PAGE_SIZE,
    statusId: "deactive",
    nameContains: null,
    emailContains: null,
    status: null,
    orderStatus: null,
    profileStatus: null,
    reason: null,
    clientId: null,
    jobId: null,
    sourceId: null,
    userId: null,
    fromDate: null,
    toDate: null,
    fadvSend: null,
  });
  const [paginationMeta, setPaginationMeta] = useState({
    pageNumber: 1,
    pageSize: DEFAULT_PAGE_SIZE,
    totalPages: 0,
    totalRecords: 0,
  });
  const [tableMetaData, setTableMetaData] = useState([]);
  const [candidateFilterMetaData, setCandidateFilterMetaData] = useState([]);

  const [jobOptions, setJobOptions] = useState([]);
  const [orderStatusOptions, setOrderStatusOptions] = useState([]);
  const [candidateEditOpen, setCandidateEditOpen] = useState(false);
  const [candidateForEdit, setCandidateForEdit] = useState(null);
  const [driverDetailsOpen, setDriverDetailsOpen] = useState(false);
  const [driverDetailsCandidateId, setDriverDetailsCandidateId] = useState(null);
  const [closureModalOpen, setClosureModalOpen] = useState(false);
  const [closureCandidateId, setClosureCandidateId] = useState(null);
  const [markAsHiredModalOpen, setMarkAsHiredModalOpen] = useState(false);
  const [markAsHiredCandidateId, setMarkAsHiredCandidateId] = useState(null);
  const [markAsRejectedModalOpen, setMarkAsRejectedModalOpen] = useState(false);
  const [markAsRejectedCandidateId, setMarkAsRejectedCandidateId] = useState(null);

  /** Option lists for toolbar `dropdown` filters (lazy-loaded via CommonFilterOptionsContext map). */
  const [toolbarDropdownReactSelectOptionsByFieldName, setToolbarDropdownReactSelectOptionsByFieldName] =
    useState({});

  const defaultTableColumns = useMemo(
    () => [
      { Header: "Client", accessor: "client" },
      { Header: "Name", accessor: "name" },
      { Header: "Email", accessor: "email" },
      { Header: "Phone", accessor: "phone" },
      { Header: "Created On", accessor: "createdOn" },
      { Header: "Job", accessor: "job" },
      { Header: "Profile Status", accessor: "profileStatus" },
      { Header: "Order Status", accessor: "orderStatus" },
      { Header: "Source", accessor: "source" },
      { Header: "Status", accessor: "status" },
    ],
    []
  );

  /**
   * When the backend marks a filter definition with `isDefault: true`, that control is surfaced in the compact
   * row above the table (parity with verify-test.jsx upper filters). Definitions without that flag stay modal-only.
   */
  const candidateMetadataToolbarFilterDefinitions = useMemo(
    () =>
      (Array.isArray(candidateFilterMetaData) ? candidateFilterMetaData : []).filter(
        (filterDefinitionFromMetadataApi) =>
          filterDefinitionFromMetadataApi?.field && filterDefinitionFromMetadataApi?.isDefault === true
      ),
    [candidateFilterMetaData]
  );

  /**
   * Non-default definitions (missing `isDefault`, or explicitly `false`) remain in AllCommonFilterModal —
   * the slide-out drawer with Save / Reset parity from other list screens.
   */
  const candidateMetadataModalOnlyFilterDefinitions = useMemo(
    () =>
      (Array.isArray(candidateFilterMetaData) ? candidateFilterMetaData : []).filter(
        (filterDefinitionFromMetadataApi) =>
          filterDefinitionFromMetadataApi?.field && filterDefinitionFromMetadataApi?.isDefault !== true
      ),
    [candidateFilterMetaData]
  );

  const usesMetadataDrivenCandidateFilterUi =
    Array.isArray(candidateFilterMetaData) && candidateFilterMetaData.length > 0;

  /**
   * Hide Reset / FILTERS when metadata says every field is a default (toolbar-only) so we do not open an empty modal.
   * Legacy pages without metadata still surface the full static modal.
   */
  const shouldShowTableHeaderFilterAndResetControls = useMemo(
    () =>
      !usesMetadataDrivenCandidateFilterUi ||
      candidateMetadataModalOnlyFilterDefinitions.length > 0,
    [usesMetadataDrivenCandidateFilterUi, candidateMetadataModalOnlyFilterDefinitions]
  );

  const activeModalFilterCount = useMemo(
    () =>
      countActiveMetadataFilters(
        candidateListFilter,
        candidateMetadataModalOnlyFilterDefinitions
      ),
    [candidateListFilter, candidateMetadataModalOnlyFilterDefinitions]
  );

  const shouldRenderCandidateMetadataToolbarFilterRow =
    candidateMetadataToolbarFilterDefinitions.length > 0;

  /**
   * Baseline empty UI state: static defaults plus any keys introduced by metadata (e.g. `statusId`) so inputs stay controlled.
   */
  const buildBaselineEmptyCandidateFilterState = useCallback(() => {
    const baselineCandidateFilterState = { ...DEFAULT_CANDIDATE_LIST_FILTER };
    (Array.isArray(candidateFilterMetaData) ? candidateFilterMetaData : []).forEach(
      (filterDefinitionFromMetadataApi) => {
        const candidateFilterFieldKey = filterDefinitionFromMetadataApi?.field;
        if (!candidateFilterFieldKey) return;
        baselineCandidateFilterState[candidateFilterFieldKey] =
          filterDefinitionFromMetadataApi?.type === "date" ? null : "";
        if (isMetadataAsyncSelectFilterDefinition(filterDefinitionFromMetadataApi)) {
          baselineCandidateFilterState[getAsyncFilterLabelFieldKey(candidateFilterFieldKey)] =
            "";
        }
      }
    );
    return baselineCandidateFilterState;
  }, [candidateFilterMetaData]);

  /** Stable filter config for AllCommonFilterModal — metadata modal slice, or the legacy static field list. */
  const allCandidatesFilterModalData = useMemo(
    () => {
      if (candidateMetadataModalOnlyFilterDefinitions.length) {
        return mapFilterDefinitionsToModalData(
          candidateMetadataModalOnlyFilterDefinitions,
          candidateListFilter
        );
      }

      if (usesMetadataDrivenCandidateFilterUi) {
        return [];
      }

      // return [
      //   {
      //     title: "Search by Email/Company",
      //     field: "search",
      //     type: "text",
      //     placeholder: "Search by Email/Company",
      //     value: candidateListFilter?.search || "",
      //   },
      //   {
      //     title: "Select Status",
      //     field: "status",
      //     type: "select",
      //     itemArray: STATUS_OPTIONS.map((item) => ({
      //       value: item.value,
      //       label: item.label,
      //     })),
      //     value: candidateListFilter?.status || "",
      //   },
      //   {
      //     title: "Select Order Status",
      //     field: "orderStatus",
      //     type: "select",
      //     itemArray: orderStatusOptions || [],
      //     value: candidateListFilter?.orderStatus || "",
      //   },
      //   {
      //     title: "Select Profile Status",
      //     field: "profileStatus",
      //     type: "select",
      //     itemArray: PROFILE_STATUS_OPTIONS.map((item) => ({
      //       value: item.value,
      //       label: item.label,
      //     })),
      //     value: candidateListFilter?.profileStatus || "",
      //   },
      //   {
      //     title: "Select Reason",
      //     field: "reason",
      //     type: "select",
      //     itemArray: REASON_OPTIONS.map((item) => ({
      //       value: item.value,
      //       label: item.label,
      //     })),
      //     value: candidateListFilter?.reason || "",
      //   },
      //   {
      //     title: "Select Client",
      //     field: "clientId",
      //     type: "asyncSelect",
      //     asyncKind: "client",
      //     value: candidateListFilter?.clientId || "",
      //   },
      //   {
      //     title: "Select Job",
      //     field: "jobId",
      //     type: "select",
      //     itemArray: jobOptions || [],
      //     value: candidateListFilter?.jobId || "",
      //   },
      //   {
      //     title: "Select Source",
      //     field: "sourceId",
      //     type: "asyncSelect",
      //     asyncKind: "source",
      //     value: candidateListFilter?.sourceId || "",
      //   },
      //   {
      //     title: "Select User",
      //     field: "userId",
      //     type: "asyncSelect",
      //     asyncKind: "user",
      //     value: candidateListFilter?.userId || "",
      //   },
      //   {
      //     title: "Start Date",
      //     field: "fromDate",
      //     type: "date",
      //     value: candidateListFilter?.fromDate || "",
      //   },
      //   {
      //     title: "End Date",
      //     field: "toDate",
      //     type: "date",
      //     value: candidateListFilter?.toDate || "",
      //   },
      //   {
      //     title: "Fadv Send",
      //     field: "fadvSend",
      //     type: "select",
      //     itemArray: FADV_SEND_OPTIONS.map((item) => ({
      //       value: item.value,
      //       label: item.label,
      //     })),
      //     value:
      //       candidateListFilter?.fadvSend === true ||
      //       candidateListFilter?.fadvSend === false
      //         ? String(candidateListFilter.fadvSend)
      //         : candidateListFilter?.fadvSend ?? "",
      //   },
      // ];
    },
    [
      candidateListFilter,
      candidateFilterMetaData,
      candidateMetadataModalOnlyFilterDefinitions,
      usesMetadataDrivenCandidateFilterUi,
      jobOptions,
      orderStatusOptions,
    ]
  );

  const isRowInEligibleForHire = (row = {}) => {
    const rawStatus =
      row?._originalCandidate?.profileStatus ??
      row?._originalCandidate?.profileStatus ??
      row?.profileStatus
    return rawStatus === "in-eligible for hire";
  };

  /** Fetch remote select options whenever the toolbar exposes new `dropdown` filters from metadata. */
  useEffect(() => {
    const toolbarDropdownDefinitionsNeedingRemoteOptions =
      candidateMetadataToolbarFilterDefinitions.filter(
        (filterDefinitionFromMetadataApi) =>
          filterDefinitionFromMetadataApi?.type === "dropdown" &&
          filterDefinitionFromMetadataApi?.field
      );
    if (!toolbarDropdownDefinitionsNeedingRemoteOptions.length) {
      setToolbarDropdownReactSelectOptionsByFieldName({});
      return;
    }
    let requestCancelled = false;
    (async () => {
      const optionListTuples = await Promise.all(
        toolbarDropdownDefinitionsNeedingRemoteOptions.map(async (filterDefinitionFromMetadataApi) => {
          const dropdownFieldBackendKey = filterDefinitionFromMetadataApi.field;
          const staticSelectOptionsProvidedByMetadata =
            Array.isArray(filterDefinitionFromMetadataApi?.itemArray) ?
              filterDefinitionFromMetadataApi.itemArray
            : [];
          if (staticSelectOptionsProvidedByMetadata.length) {
            return [dropdownFieldBackendKey, staticSelectOptionsProvidedByMetadata];
          }
          const remoteOptionsFetchedForApiKey =
            await getDropdownOptionsByApiKey(filterDefinitionFromMetadataApi?.api);
          return [dropdownFieldBackendKey, remoteOptionsFetchedForApiKey];
        })
      );
      if (requestCancelled) return;
      const aggregatedOptionsKeyedByBackendFieldName = {};
      optionListTuples.forEach(([dropdownFieldBackendKey, reactSelectCompatibleOptions]) => {
        aggregatedOptionsKeyedByBackendFieldName[dropdownFieldBackendKey] =
          reactSelectCompatibleOptions;
      });
      setToolbarDropdownReactSelectOptionsByFieldName(aggregatedOptionsKeyedByBackendFieldName);
    })();
    return () => {
      requestCancelled = true;
    };
  }, [candidateMetadataToolbarFilterDefinitions, getDropdownOptionsByApiKey]);

  /** Ensure every backend filter key exists inside local React state once metadata resolves (controlled inputs everywhere). */
  useEffect(() => {
    if (!Array.isArray(candidateFilterMetaData) || !candidateFilterMetaData.length) return;
    setCandidateListFilter((previousCandidateFilterState) => {
      const mergedCandidateFilterState = { ...previousCandidateFilterState };
      let mergedNewKeysIntoState = false;
      candidateFilterMetaData.forEach((filterDefinitionFromMetadataApi) => {
        const backendFilterFieldIdentifier = filterDefinitionFromMetadataApi?.field;
        if (!backendFilterFieldIdentifier) return;
        if (!(backendFilterFieldIdentifier in mergedCandidateFilterState)) {
          mergedCandidateFilterState[backendFilterFieldIdentifier] =
            filterDefinitionFromMetadataApi?.type === "date" ? null : "";
          mergedNewKeysIntoState = true;
        }
        if (isMetadataAsyncSelectFilterDefinition(filterDefinitionFromMetadataApi)) {
          const asyncLabelFieldKey = getAsyncFilterLabelFieldKey(backendFilterFieldIdentifier);
          if (!(asyncLabelFieldKey in mergedCandidateFilterState)) {
            mergedCandidateFilterState[asyncLabelFieldKey] = "";
            mergedNewKeysIntoState = true;
          }
        }
      });
      return mergedNewKeysIntoState ? mergedCandidateFilterState : previousCandidateFilterState;
    });
  }, [candidateFilterMetaData]);

  const formatDateMMDDYYYY = (value) => {
    if (!value) return "";
    const d = dayjs(value);
    return d.isValid() ? d.format("MM-DD-YYYY") : String(value).slice(0, 10);
  };

  /** Metadata field candidateStatus → table accessor `status`; API may send candidateStatus or status. */
  const formatIsUSCitizenLabel = (item) => {
    if (item === null || item === undefined) return "";
    if (item === 1) return "US Citizen";
    if (item === 0) return "Non USA Citizen";
    if (item === -1) return "N/A";
    return String(item);
  };

  /** Table display: null / undefined / blank → "N/A" (used for status, state, city). */
  const naIfEmpty = (item) => {
    if (item === null || item === undefined || item === "null") return "N/A";
    if (typeof item === "string" && item.trim() === "") return "N/A";
    return item;
  };

  const mapRecordsToRows = (list = []) =>
    list.map((item = {}) => {
      const first = item?.firstName || "";
      const last = item?.lastName || "";
      const fullName = `${first} ${last}`.trim() || item?.name || "";
      const createdFromCreateAt = item?.createAt ?? item?.createdOn ?? item?.createdDate;
      const rawStatus = item?.candidateStatus ?? item?.status;
      return {
        ...item,
        _originalCandidate: { ...item },
        id: item?.id || item?.candidateId || "",
        client: item?.clientName || item?.client || "",
        name: fullName,
        email: item?.email || "",
        phone: phoneNumberShowFormat(item?.phone || item?.phoneNumber || ""),
        createdOn: formatDateMMDDYYYY(createdFromCreateAt),
        createdDate: formatDateMMDDYYYY(createdFromCreateAt),
        job: item?.jobName || item?.job || "",
        profileStatus: item?.profileStatus ?? "",
        orderStatus: item?.orderStatus ?? "",
        mecStatus: item?.mecStatus ?? "",
        drugStatus: item?.drugStatus ?? "",
        updateSend: item?.updateSend,
        fadvSend: item?.fadvSend ?? item?.isFadvSent,
        isFadvSent: item?.isFadvSent ?? item?.fadvSend,
        funnelStatus: item?.funnelStatus ?? item?.funnlStatus ?? "",
        funnlStatus: item?.funnlStatus ?? item?.funnelStatus ?? "",
        source: item?.sourceName || item?.source || "",
        status: naIfEmpty(rawStatus),
        state: naIfEmpty(item?.state),
        city: naIfEmpty(item?.city),
        isUSCitizen: formatIsUSCitizenLabel(item?.isUsCitizen ?? item?.isUSCitizen),
      };
    });

  /** Translate the on-page filter model (toolbar + drawer) into the POST body Candidate filter endpoint expects. */
  const buildPayloadFromFilterForm = (form = {}, overrides = {}) => {
    const searchKeywordTrimmed = String(form?.search || "").trim();
    const fromDateForFilter = normalizeMetadataFilterDateForApi(form?.fromDate);
    const toDateForFilter = normalizeMetadataFilterDateForApi(form?.toDate);
    const statusOrStatusIdentifierValueFromForm =
      form?.status ?? form?.statusId ?? null;
    let fadvSentBooleanOrNullPayloadValue = form?.fadvSend ?? form?.isFadvSent;
    if (fadvSentBooleanOrNullPayloadValue === "true") {
      fadvSentBooleanOrNullPayloadValue = true;
    } else if (fadvSentBooleanOrNullPayloadValue === "false") {
      fadvSentBooleanOrNullPayloadValue = false;
    } else {
      fadvSentBooleanOrNullPayloadValue = null;
    }

    /**
     * First copy every backend-declared filter field verbatim so dynamically named IDs (beyond the legacy constants)
     * still reach `/Candidate/submit`-style handlers; canonical keys below intentionally override overlaps.
     */
    const looselyMappedMetadataFilterValuesFromUiFormLayer = {};
    if (Array.isArray(candidateFilterMetaData)) {
      candidateFilterMetaData.forEach((filterDefinitionFromMetadataApi) => {
        const backendFilterFieldIdentifier = filterDefinitionFromMetadataApi?.field;
        if (!backendFilterFieldIdentifier || backendFilterFieldIdentifier === "search") return;
        looselyMappedMetadataFilterValuesFromUiFormLayer[backendFilterFieldIdentifier] =
          form?.[backendFilterFieldIdentifier];
      });
    }

    return normalizeCandidateFilterPayload(
      {
        ...looselyMappedMetadataFilterValuesFromUiFormLayer,
        pageNumber: 1,
        pageSize: requestPayload.pageSize || DEFAULT_PAGE_SIZE,
        nameContains: normalizeOptionalStringFilterValueOrNull(searchKeywordTrimmed),
        emailContains: normalizeOptionalStringFilterValueOrNull(searchKeywordTrimmed),
        status:
          normalizeOptionalStringFilterValueOrNull(statusOrStatusIdentifierValueFromForm),
        orderStatus:
          normalizeOptionalStringFilterValueOrNull(form?.orderStatus),
        profileStatus:
          normalizeOptionalStringFilterValueOrNull(form?.profileStatus),
        reason: normalizeOptionalStringFilterValueOrNull(form?.reason),
        clientId:
          normalizeOptionalStringFilterValueOrNull(form?.clientId),
        jobId: normalizeOptionalStringFilterValueOrNull(form?.jobId),
        sourceId:
          normalizeOptionalStringFilterValueOrNull(form?.sourceId),
        userId:
          normalizeOptionalStringFilterValueOrNull(form?.userId),
        fromDate: fromDateForFilter,
        toDate: toDateForFilter,
        fadvSend: fadvSentBooleanOrNullPayloadValue,
        ...overrides,
      },
      candidateFilterMetaData
    );
  };

  const getCandidateList = async (payloadOverrides = {}, options = {}) => {
    const { onlyPagination = false } = options;
    setLoading(true);
    try {
      const payload = onlyPagination
        ? {
            pageNumber: Number(payloadOverrides.pageNumber) || 1,
            pageSize: Number(payloadOverrides.pageSize) || DEFAULT_PAGE_SIZE,
            statusId: 'deactive'
          }
        : normalizeCandidateFilterPayload(
            {
              ...requestPayload,
              ...payloadOverrides,
            },
            candidateFilterMetaData
          );

      const response = await submitCandidateFilterData(payload);
      const responseData = response?.data ?? response ?? {};
      const listData =
        responseData?.records ??
        responseData?.data ??
        (Array.isArray(responseData) ? responseData : []);

      const records = Array.isArray(listData) ? listData : [];
      setCandidateData(mapRecordsToRows(records));

      if (response == null || response?.success !== false) {
        setPaginationMeta({
          pageNumber: Number(responseData?.pageNumber) || Number(payload?.pageNumber) || 1,
          pageSize: Number(responseData?.pageSize) || Number(payload?.pageSize) || DEFAULT_PAGE_SIZE,
          totalPages: Number(responseData?.totalPages) || 0,
          totalRecords: Number(responseData?.totalRecords) || records.length,
        });
        if (onlyPagination) {
          const clearedServerFilterSnapshotAfterPaginationReset =
            normalizeCandidateFilterPayload(
              {
                pageNumber: payload.pageNumber,
                pageSize: payload.pageSize,
                statusId: "deactive",
                nameContains: null,
                emailContains: null,
                status: null,
                orderStatus: null,
                profileStatus: null,
                reason: null,
                clientId: null,
                jobId: null,
                sourceId: null,
                userId: null,
                fromDate: null,
                toDate: null,
                fadvSend: null,
                ...Object.fromEntries(
                  (Array.isArray(candidateFilterMetaData) ? candidateFilterMetaData : [])
                    .filter(
                      (filterDefinitionFromMetadataApi) =>
                        filterDefinitionFromMetadataApi?.field &&
                        filterDefinitionFromMetadataApi.field !== "search"
                    )
                    .map((filterDefinitionFromMetadataApi) => [
                      filterDefinitionFromMetadataApi.field,
                      null,
                    ])
                ),
              },
              candidateFilterMetaData
            );
          setRequestPayload(clearedServerFilterSnapshotAfterPaginationReset);
          setCandidateListFilter(buildBaselineEmptyCandidateFilterState());
        } else {
          setRequestPayload(
            normalizeCandidateFilterPayload(payload, candidateFilterMetaData)
          );
        }
      } else {
        setCandidateData([]);
        if (response) {
          toast.error(response?.message || "Unable to load candidates");
        }
      }
    } catch (err) {
      toast.error(err?.message || "Unable to load candidates");
      setCandidateData([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchFilterDropdowns = async () => {
    try {
      const [jobOptionsList, orderStatusRes] = await Promise.all([
        getDropdownOptionsByApiKey("get_job_options"),
        fetchCommonOrderStatusOptions(),
      ]);
      const orderStatusRecords = orderStatusRes?.data || [];
      setJobOptions(Array.isArray(jobOptionsList) ? jobOptionsList : []);
      setOrderStatusOptions(
        (Array.isArray(orderStatusRecords) ? orderStatusRecords : [])
          .map((item) => ({
            value: String(item?.value || "").trim(),
            label: item?.data || item?.value || "Order Status",
          }))
          .filter((opt) => opt?.value)
      );
    } catch (e) {
      toast.error(e?.message || "Unable to load filter lists");
    }
  };

  useEffect(() => {
    const bootstrap = async () => {
      setLoading(true);
      try {
        const metaRes = await fetchMetadataSchema("metadata:candidate", () => getCandidateMetadata());
        const meta = metaRes?.data ?? metaRes ?? {};
        const metadataFilters = Array.isArray(meta?.filters) ? meta.filters : [];

        const tableColumns = meta?.table?.columns?.length
          ? meta.table.columns.map((col) => {
              const accessor = pascalToCamel(col?.field);
              if (accessor === "candidateStatus") {
                return mapApiMetadataToTableColumnDefinition(col, {
                  resolveAccessor: () => "status",
                });
              }
              return mapApiMetadataToTableColumnDefinition(col);
            })
          : defaultTableColumns;
        setTableMetaData(tableColumns);
        setCandidateFilterMetaData(metadataFilters);
      } catch (e) {
        toast.error(e?.message || "Unable to load candidate metadata");
        setTableMetaData(defaultTableColumns);
        setCandidateFilterMetaData([]);
      }

      await fetchFilterDropdowns();
      setLoading(false);
    };
    bootstrap();
  }, []);

  /**
   * Modal submit should only replace modal-owned fields so toolbar defaults (e.g. `search`) continue to apply
   * until the user edits them — mirroring how verify-test keeps its upper row independent.
   */
  const handleAllCandidatesFilterSubmit = async (filterFormValuesFromModal = {}) => {
    const mergedCandidateListFilterAfterModalSave = mergeModalSubmitIntoListFilter(
      candidateListFilter,
      allCandidatesFilterModalData,
      filterFormValuesFromModal
    );
    setCandidateListFilter(mergedCandidateListFilterAfterModalSave);
    setCommonFilterModalOpen(false);
    setCandidateFilterApplied(true);
    const nextServerFilterPayload = buildPayloadFromFilterForm(
      mergedCandidateListFilterAfterModalSave
    );
    await getCandidateList(nextServerFilterPayload);
  };

  const handleAllCandidatesFilterReset = async () => {
    setCandidateFilterApplied(false);
    const resetCandidateFilterStateMatchingMetadataKeys = buildBaselineEmptyCandidateFilterState();
    setCandidateListFilter(resetCandidateFilterStateMatchingMetadataKeys);
    setCommonFilterModalOpen(false);
    setCandidateData([]);
    setPaginationMeta({
      pageNumber: 1,
      pageSize: requestPayload.pageSize || DEFAULT_PAGE_SIZE,
      totalPages: 0,
      totalRecords: 0,
    });
    setRequestPayload(
      normalizeCandidateFilterPayload(
        {
          pageNumber: 1,
          pageSize: requestPayload.pageSize || DEFAULT_PAGE_SIZE,
          statusId: "deactive",
          nameContains: null,
          emailContains: null,
          status: null,
          orderStatus: null,
          profileStatus: null,
          reason: null,
          clientId: null,
          jobId: null,
          sourceId: null,
          userId: null,
          fromDate: null,
          toDate: null,
          fadvSend: null,
          ...Object.fromEntries(
            (Array.isArray(candidateFilterMetaData) ? candidateFilterMetaData : [])
              .filter(
                (filterDefinitionFromMetadataApi) =>
                  filterDefinitionFromMetadataApi?.field &&
                  filterDefinitionFromMetadataApi.field !== "search"
              )
              .map((filterDefinitionFromMetadataApi) => [
                filterDefinitionFromMetadataApi.field,
                null,
              ])
          ),
        },
        candidateFilterMetaData
      )
    );
  };

  /** Toolbar Search button / Enter key: push the current on-page filter object to the server without opening the modal. */
  const handleUpperToolbarCandidateFilterSearch = async () => {
    setCandidateFilterApplied(true);
    const nextServerFilterPayloadFromToolbar = buildPayloadFromFilterForm(candidateListFilter);
    await getCandidateList(nextServerFilterPayloadFromToolbar);
  };

  const handleToolbarTextFilterInputChange = (backendFilterFieldKey, nextInputStringValue) => {
    setCandidateListFilter((previousCandidateFilterState) => ({
      ...previousCandidateFilterState,
      [backendFilterFieldKey]: nextInputStringValue ?? "",
    }));
  };

  const handleToolbarReactSelectFilterChange = (backendFilterFieldKey, nextSelectedOption) => {
    setCandidateListFilter((previousCandidateFilterState) => ({
      ...previousCandidateFilterState,
      [backendFilterFieldKey]: nextSelectedOption?.value ?? "",
      [getAsyncFilterLabelFieldKey(backendFilterFieldKey)]:
        nextSelectedOption?.label ?? "",
    }));
  };

  useEffect(() => {
    if (!commonResetFilter) return;
    const run = async () => {
      await handleAllCandidatesFilterReset();
    };
    run();
  }, [commonResetFilter]);

  const handlePageChange = async (page) => {
    if (!candidateFilterApplied) return;
    await getCandidateList({ pageNumber: page });
  };

  const handleRowsPerPageChange = async (rows) => {
    if (!candidateFilterApplied) return;
    await getCandidateList({ pageNumber: 1, pageSize: rows });
  };

  const handleDetails = (row) => {
    // console.log('......row', row)
    // navigate(`/${urlEndPoint.callDetails}/${row?.id }`);
    window.open(`/${urlEndPoint.callDetails}/${row?.id}`, "_blank", "noopener,noreferrer");
    
  };

  const handleCandidateEdit = (row) => {
    const raw = row?._originalCandidate ?? row;
    setCandidateForEdit(raw && typeof raw === "object" ? { ...raw } : {});
    setCandidateEditOpen(true);
  };

  const closeCandidateEditModal = () => {
    setCandidateEditOpen(false);
    setCandidateForEdit(null);
  };

  const resolveCandidateRowId = (row = {}) =>
    String(row?.id ?? row?._id ?? row?.candidateId ?? "").trim();

  /** Merge API/form updates into one visible row without re-running the filter POST. */
  const applyCandidateRowPatch = useCallback((candidateId, partialRawUpdate = {}) => {
    const targetId = String(candidateId ?? "").trim();
    if (!targetId || !partialRawUpdate || typeof partialRawUpdate !== "object") return;
    setCandidateData((previousRows) => {
      let rowMatched = false;
      const nextRows = previousRows.map((tableRow) => {
        if (resolveCandidateRowId(tableRow) !== targetId) return tableRow;
        rowMatched = true;
        const mergedRawCandidateRecord = {
          ...(tableRow._originalCandidate || {}),
          ...partialRawUpdate,
          id: partialRawUpdate?.id ?? tableRow._originalCandidate?.id ?? targetId,
          candidateId:
            partialRawUpdate?.candidateId ??
            partialRawUpdate?.id ??
            tableRow._originalCandidate?.candidateId ??
            targetId,
        };
        const [mappedTableRow] = mapRecordsToRows([mergedRawCandidateRecord]);
        return mappedTableRow;
      });
      return rowMatched ? nextRows : previousRows;
    });
  }, []);

  const handleCandidateEditSuccess = useCallback(
    (updatedRawCandidate) => {
      if (!updatedRawCandidate || typeof updatedRawCandidate !== "object") return;
      const targetId = String(
        updatedRawCandidate?.id ?? updatedRawCandidate?.candidateId ?? ""
      ).trim();
      if (!targetId) return;
      applyCandidateRowPatch(targetId, updatedRawCandidate);
    },
    [applyCandidateRowPatch]
  );

  /** Remove deactivated rows locally and adjust total count. */
  const handleDeactivateCandidatesSuccess = useCallback((deactivatedIds = []) => {
    const deactivatedIdSet = new Set(
      (Array.isArray(deactivatedIds) ? deactivatedIds : [])
        .map((id) => String(id).trim())
        .filter(Boolean)
    );
    if (!deactivatedIdSet.size) return;
    setCandidateData((previousRows) => {
      const nextRows = previousRows.filter(
        (tableRow) => !deactivatedIdSet.has(resolveCandidateRowId(tableRow))
      );
      const removedCount = previousRows.length - nextRows.length;
      if (removedCount > 0) {
        setPaginationMeta((previousPagination) => ({
          ...previousPagination,
          totalRecords: Math.max(
            0,
            (Number(previousPagination?.totalRecords) || 0) - removedCount
          ),
        }));
      }
      return nextRows;
    });
  }, []);

  const extractCandidatePatchFromApiResponse = (response) => {
    const responseData = response?.data;
    if (!responseData || typeof responseData !== "object") return {};
    if (Array.isArray(responseData)) {
      const firstRecord = responseData[0];
      return firstRecord && typeof firstRecord === "object" ? firstRecord : {};
    }
    if (responseData.record && typeof responseData.record === "object") {
      return responseData.record;
    }
    if (responseData.candidate && typeof responseData.candidate === "object") {
      return responseData.candidate;
    }
    if (Array.isArray(responseData.records) && responseData.records[0]) {
      return responseData.records[0];
    }
    return responseData;
  };

  const candidateRowPatchIncludesStatusFields = (patch = {}) =>
    CANDIDATE_ROW_STATUS_SOURCE_KEYS.some(
      (fieldKey) => patch[fieldKey] !== undefined && patch[fieldKey] !== null
    );

  const fetchCandidateRowPatchById = async (candidateId) => {
    const response = await getCandidateDetailsForUsingCallDetails({
      candidateId,
    });
    const rowData = response?.data ?? response;
    if (!rowData || typeof rowData !== "object" || Array.isArray(rowData)) return null;
    const id = String(candidateId ?? "").trim();
    return {
      ...rowData,
      id: rowData?.id ?? id,
      candidateId: rowData?.candidateId ?? id,
    };
  };

  const handleCandidateAddClosure = (row) => {
    const cid = resolveCandidateRowId(row);
    if (!cid) {
      toast.error("Candidate ID not found");
      return;
    }
    setClosureCandidateId(cid);
    setClosureModalOpen(true);
  };

  const closeClosureModal = () => {
    setClosureModalOpen(false);
    setClosureCandidateId(null);
  };

  const handleCandidateMarkAsHired = (row) => {
    const cid = resolveCandidateRowId(row);
    if (!cid) {
      toast.error("Candidate ID not found");
      return;
    }
    setMarkAsHiredCandidateId(cid);
    setMarkAsHiredModalOpen(true);
  };

  const closeMarkAsHiredModal = () => {
    setMarkAsHiredModalOpen(false);
    setMarkAsHiredCandidateId(null);
  };

  const handleCandidateMarkAsRejected = (row) => {
    const cid = resolveCandidateRowId(row);
    if (!cid) {
      toast.error("Candidate ID not found");
      return;
    }
    setMarkAsRejectedCandidateId(cid);
    setMarkAsRejectedModalOpen(true);
  };

  const closeMarkAsRejectedModal = () => {
    setMarkAsRejectedModalOpen(false);
    setMarkAsRejectedCandidateId(null);
  };

  const handleTodayCall = async (row) => {
    const id = resolveCandidateRowId(row);
    if (!id) {
      toast.error("Candidate ID not found");
      return;
    }
    const swalResult = await Swal.fire({
      title: "Add today’s call?",
      text: "Are you sure you want to add today’s call for this candidate?",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes, add",
      cancelButtonText: "Cancel",
      reverseButtons: true,
    });
    if (!swalResult?.isConfirmed) return;
    const response = await candidateTodayCall(id);
    if (response?.success || response?.isSuccess) {
      toast.success(response?.message || "Today’s call updated");
      const patchFromApi = extractCandidatePatchFromApiResponse(response);
      if (Object.keys(patchFromApi).length) {
        applyCandidateRowPatch(id, { id, candidateId: id, ...patchFromApi });
      }
      return;
    }
    toast.error(response?.message || "Unable to add today’s call");
  };

  const handleSureMailSend = async (row) => {
    const id = resolveCandidateRowId(row);
    if (!id) {
      toast.error("Candidate ID not found");
      return;
    }
    const swalResult = await Swal.fire({
      title: "Resend mail?",
      text: "Are you sure you want to resend mail for this candidate?",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes, resend",
      cancelButtonText: "Cancel",
      reverseButtons: true,
    });
    if (!swalResult?.isConfirmed) return;
    const response = await candidateSureMailSend(id);
    if (response?.success || response?.isSuccess) {
      toast.success(response?.message || "Mail sent");
      return;
    }
    toast.error(response?.message || "Unable to resend mail");
  };

  const handleResetCandidate = async (row) => {
    const id = resolveCandidateRowId(row);
    if (!id) {
      toast.error("Candidate ID not found");
      return;
    }
    const swalResult = await Swal.fire({
      title: "Reset Candidate?",
      text: "Sure to Reset this candidate?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, reset",
      cancelButtonText: "Cancel",
      reverseButtons: true,
    });
    if (!swalResult?.isConfirmed) return;
    const response = await resetCandidate(id);
    if (response?.success || response?.isSuccess) {
      toast.success(response?.message);
      let rowPatch = { id, candidateId: id, ...extractCandidatePatchFromApiResponse(response) };
      if (!candidateRowPatchIncludesStatusFields(rowPatch)) {
        const fetchedRow = await fetchCandidateRowPatchById(id);
        if (fetchedRow) {
          rowPatch = { ...fetchedRow, ...rowPatch };
        }
      }
      applyCandidateRowPatch(id, rowPatch);
      return;
    }
    toast.error(response?.message || "Unable to reset candidate");
  };

  const handleDriverDetails = (row) => {
    const candidateId = row?.candidateId;
    if (!candidateId) {
      toast.error("Candidate ID not found");
      return;
    }
    setDriverDetailsCandidateId(String(candidateId));
    setDriverDetailsOpen(true);
  };

  const closeDriverDetailsModal = () => {
    setDriverDetailsOpen(false);
    setDriverDetailsCandidateId(null);
  };

  const filteredData = useMemo(() => candidateData, [candidateData]);
  const candidateActionPermissions = useMemo(
    () => ({
      canEdit: Boolean(permissionData?.CandidateUpdateAccess),
      canCallDetails: Boolean(
        permissionData?.CallCallDetailsAccess || permissionData?.CallDetailsAccess
      ),
      canDriverDetails: Boolean(permissionData?.CandidateDriverDetailsAccess),
      canTodayCall: Boolean(permissionData?.CandidateAddTodayAccess),
      canSureMailSend: Boolean(permissionData?.CandidateResetMailSend),
      canResetCandidate: Boolean(permissionData?.CandidateResetAccess),
      canAddClosure: Boolean(permissionData?.CandidateModifyClosureAccess),
      canMarkAsHired: Boolean(permissionData?.CandidateHiredAccess),
      canMarkAsRejected: Boolean(permissionData?.CandidateRejectedAccess),
      canBulkDeactivate: Boolean(permissionData?.CandidateDeactiveMultipleAccess),
    }),
    [permissionData]
  );
  const hasAnyCandidateRowAction = useMemo(() => {
    const { canBulkDeactivate, ...rowActions } = candidateActionPermissions;
    return Object.values(rowActions).some(Boolean);
  }, [candidateActionPermissions]);

  return (
    <div className="page-content">
      <Container fluid>
        <Card>
          <CardBody>
            <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
              <h5 className="fw-bold mb-0">Candidates</h5>
            </div>
            <hr />

            {/**
             * Compact filter row driven solely by metadata `isDefault: true` entries (UX alignment with verify-test.jsx).
             */}
            {shouldRenderCandidateMetadataToolbarFilterRow ? (
              <MetadataFilterToolbar
                toolbarDefinitions={candidateMetadataToolbarFilterDefinitions}
                listFilter={candidateListFilter}
                onTextChange={handleToolbarTextFilterInputChange}
                onSelectChange={handleToolbarReactSelectFilterChange}
                onSearch={handleUpperToolbarCandidateFilterSearch}
                onClear={handleAllCandidatesFilterReset}
                showClearWhenNoModalFilters={
                  !shouldShowTableHeaderFilterAndResetControls
                }
                toolbarDropdownOptionsByFieldName={
                  toolbarDropdownReactSelectOptionsByFieldName
                }
                layoutPreset="candidates"
                pageName="MyCall"
              />
            ) : null}

            {loading ? (
             <Loaders />
            ) : !tableMetaData?.length ? (
              <div className="text-muted text-center py-5">Unable to load table columns.</div>
            ) : (
              <CandidateOnboardingTableContainer
                columns={tableMetaData}
                data={candidateFilterApplied ? filteredData : []}
                emptyTableContent={
                  !candidateFilterApplied ? (
                    <CandidateListFilterPrompt
                      variant="table"
                      title="Apply filters to view deactivated candidates"
                      description="To load deactivated candidate records, choose your filter criteria above and click Search. Your results will appear in this table."
                      onSearch={handleUpperToolbarCandidateFilterSearch}
                    />
                  ) : undefined
                }
                getRowClassName={(row) =>
                  isRowInEligibleForHire(row) ? 'candidate-row-ineligible-for-hire' : ""
                }
                isGlobalFilter={false}
                isSort={true}
                isActionRequired={hasAnyCandidateRowAction}
                tableClass="align-middle table-nowrap mb-0"
                theadClass="table-light"
                onDetails={
                  candidateActionPermissions.canCallDetails ? handleDetails : undefined
                }
                onEdit={
                  candidateActionPermissions.canEdit ? handleCandidateEdit : undefined
                }
                onAddClosure={
                  candidateActionPermissions.canAddClosure
                    ? handleCandidateAddClosure
                    : undefined
                }
                onMarkAsHired={
                  candidateActionPermissions.canMarkAsHired
                    ? handleCandidateMarkAsHired
                    : undefined
                }
                onMarkAsRejected={
                  candidateActionPermissions.canMarkAsRejected
                    ? handleCandidateMarkAsRejected
                    : undefined
                }
                onTodayCall={
                  candidateActionPermissions.canTodayCall ? handleTodayCall : undefined
                }
                onSureMailSend={
                  candidateActionPermissions.canSureMailSend
                    ? handleSureMailSend
                    : undefined
                }
                onResetCandidate={
                  candidateActionPermissions.canResetCandidate
                    ? handleResetCandidate
                    : undefined
                }
                onDriverDetails={
                  candidateActionPermissions.canDriverDetails
                    ? handleDriverDetails
                    : undefined
                }
                pageName="MyCall"
                isPagination={true}
                currentPage={paginationMeta?.pageNumber}
                rowsPerPage={paginationMeta?.pageSize}
                totalPages={paginationMeta?.totalPages}
                totalRecords={paginationMeta?.totalRecords}
                onPageChange={handlePageChange}
                onPageSizeChange={handleRowsPerPageChange}
                setCommonFilterModalOpen={setCommonFilterModalOpen}
                setCommonResetFilter={setCommonResetFilter}
                allCommonFilter={shouldShowTableHeaderFilterAndResetControls}
                activeFilterCount={activeModalFilterCount}
                onDeactivateSuccess={handleDeactivateCandidatesSuccess}
                isCheckboxLeftSide={false}
              />
            )}
          </CardBody>
        </Card>

        <AllCommonFilterModal
          setCommonFilterModalOpen={setCommonFilterModalOpen}
          commonFilterModalOpen={commonFilterModalOpen}
          modalTitle="Candidates Filter"
          handleSubmit={handleAllCandidatesFilterSubmit}
          filterData={allCandidatesFilterModalData}
          commonResetFilter={commonResetFilter}
          setCommonResetFilter={setCommonResetFilter}
          pageName="MyCall"
        />

        <AddEditCandidateModal
          isOpen={candidateEditOpen}
          onClose={closeCandidateEditModal}
          mode="edit"
          candidate={candidateForEdit || {}}
          onSuccess={handleCandidateEditSuccess}
        />

        <DriverDetailsModal
          isOpen={driverDetailsOpen}
          onClose={closeDriverDetailsModal}
          candidateId={driverDetailsCandidateId}
          onSuccess={handleCandidateEditSuccess}
        />

        <CandidateClosureModal
          isOpen={closureModalOpen}
          onClose={closeClosureModal}
          candidateId={closureCandidateId}
          onSuccess={handleCandidateEditSuccess}
        />

        <CandidateMarkAsHiredModal
          isOpen={markAsHiredModalOpen}
          onClose={closeMarkAsHiredModal}
          candidateId={markAsHiredCandidateId}
          onSuccess={handleCandidateEditSuccess}
        />

        <CandidateMarkAsRejectedModal
          isOpen={markAsRejectedModalOpen}
          onClose={closeMarkAsRejectedModal}
          candidateId={markAsRejectedCandidateId}
          onSuccess={handleCandidateEditSuccess}
        />
      </Container>
    </div>
  );
};

DeactivateCandidate.propTypes = {
  orders: PropTypes.array,
  onGetOrders: PropTypes.func,
};

export default withRouter(DeactivateCandidate);
